// src/hooks/useActivityTracking.ts
// Hook para rastrear atividade do administrador - ✅ VERSÃO FINAL CORRIGIDA

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const API_BASE_URL = '/api';

interface ActivityTrackingConfig {
    enabled: boolean;
    isAdmin: boolean;
    userId?: string;
}

export const useActivityTracking = (config: ActivityTrackingConfig) => {
    const { enabled, isAdmin, userId } = config;
    const location = useLocation();

    const sessionIdRef = useRef<string | null>(null);
    const pingIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const isTrackingRef = useRef(false);

    // ========== INICIAR SESSÃO ==========
    const startSession = async () => {
        if (!enabled || !isAdmin || isTrackingRef.current) return;

        try {
            const token = localStorage.getItem('accessToken');

            // ✅ Verificar se tem token antes de tentar
            if (!token) {
                console.warn('⚠️ [TRACKING] Token não encontrado, não iniciando sessão');
                return;
            }

            const sessionId = `session-${Date.now()}-${userId}`;

            console.log('▶️ [TRACKING] Iniciando sessão:', sessionId);

            const response = await fetch(`${API_BASE_URL}/activity/start-session`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    sessionId,
                    currentPage: location.pathname
                })
            });

            // ✅ Tratamento de erro 401
            if (response.status === 401) {
                console.warn('⚠️ [TRACKING] Token expirado, não iniciando tracking');
                return;
            }

            if (response.ok) {
                const data = await response.json();
                sessionIdRef.current = data.sessionId;
                isTrackingRef.current = true;

                console.log('✅ [TRACKING] Sessão iniciada:', data.sessionId);

                // Iniciar ping periódico
                startPinging();
            } else {
                console.error('❌ [TRACKING] Erro ao iniciar sessão:', response.status);
            }
        } catch (error) {
            console.error('❌ [TRACKING] Erro ao iniciar sessão:', error);
        }
    };

    // ========== PING PERIÓDICO - ✅ CORRIGIDO ==========
    const startPinging = () => {
        if (pingIntervalRef.current) return;

        pingIntervalRef.current = setInterval(async () => {
            if (!sessionIdRef.current) return;

            try {
                const token = localStorage.getItem('accessToken');

                // ✅ Verificar token antes de pingar
                if (!token) {
                    console.warn('⚠️ [TRACKING] Token não encontrado, parando pings');
                    stopTracking();
                    return;
                }

                const response = await fetch(`${API_BASE_URL}/activity/ping`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        sessionId: sessionIdRef.current,
                        currentPage: location.pathname
                    })
                });

                // ✅ Se retornar 401, parar tracking SILENCIOSAMENTE
                if (response.status === 401) {
                    console.warn('⚠️ [TRACKING] Token expirado durante ping, parando tracking');
                    stopTracking();
                    return;
                }

                if (response.ok) {
                    // ✅ Ping silencioso quando funciona (sem log para não poluir)
                    // console.log('🔄 [TRACKING] Ping enviado');
                } else {
                    console.warn('⚠️ [TRACKING] Ping falhou:', response.status);
                }
            } catch (error) {
                // ✅ Erro silencioso em pings (muito comum e esperado)
                // Apenas logar se for algo crítico
                if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
                    // Erro de rede - ignorar silenciosamente
                    return;
                }
                console.error('❌ [TRACKING] Erro no ping:', error);
            }
        }, 120000); // 30 segundos
    };

    // ========== ✅ NOVO: PARAR TRACKING ==========
    const stopTracking = () => {
        if (pingIntervalRef.current) {
            clearInterval(pingIntervalRef.current);
            pingIntervalRef.current = null;
        }
        isTrackingRef.current = false;
        // Não limpar sessionIdRef aqui para permitir end-session
    };

    // ========== ENCERRAR SESSÃO ==========
    const endSession = async () => {
        if (!sessionIdRef.current || !isTrackingRef.current) return;

        try {
            const token = localStorage.getItem('accessToken');

            // ✅ Se não tem token, apenas limpar estado local
            if (!token) {
                console.warn('⚠️ [TRACKING] Sem token, apenas limpando estado local');
                stopTracking();
                sessionIdRef.current = null;
                return;
            }

            console.log('⏹️ [TRACKING] Encerrando sessão:', sessionIdRef.current);

            const response = await fetch(`${API_BASE_URL}/activity/end-session`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    sessionId: sessionIdRef.current
                })
            });

            if (response.ok) {
                console.log('✅ [TRACKING] Sessão encerrada');
            } else if (response.status === 401) {
                console.warn('⚠️ [TRACKING] Token expirado ao encerrar (normal)');
            }
        } catch (error) {
            // ✅ Erro esperado ao fechar navegador - ignorar
            console.error('❌ [TRACKING] Erro ao encerrar sessão:', error);
        } finally {
            // Limpar estados
            stopTracking();
            sessionIdRef.current = null;
        }
    };

    // ========== EFEITOS ==========

    // Iniciar tracking ao montar
    useEffect(() => {
        if (enabled && isAdmin) {
            startSession();
        }

        // Cleanup ao desmontar
        return () => {
            if (isTrackingRef.current) {
                endSession();
            }
        };
        // eslint-disable-next-line
    }, [enabled, isAdmin]);

    // Atualizar página atual quando mudar de rota
    useEffect(() => {
        const updateCurrentPage = async () => {
            if (!sessionIdRef.current || !isTrackingRef.current) return;

            try {
                const token = localStorage.getItem('accessToken');

                // ✅ Verificar token
                if (!token) {
                    console.warn('⚠️ [TRACKING] Token não encontrado ao trocar página');
                    stopTracking();
                    return;
                }

                const response = await fetch(`${API_BASE_URL}/activity/ping`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        sessionId: sessionIdRef.current,
                        currentPage: location.pathname
                    })
                });

                // ✅ Tratar 401
                if (response.status === 401) {
                    console.warn('⚠️ [TRACKING] Token expirado ao trocar página');
                    stopTracking();
                    return;
                }

                if (response.ok) {
                    console.log('📄 [TRACKING] Página atualizada:', location.pathname);
                }
            } catch (error) {
                // ✅ Erro silencioso
                console.error('❌ [TRACKING] Erro ao atualizar página:', error);
            }
        };

        if (isTrackingRef.current) {
            updateCurrentPage();
        }
    }, [location.pathname]);

    // Encerrar sessão ao fechar aba/navegador
    useEffect(() => {
        const handleBeforeUnload = () => {
            if (isTrackingRef.current) {
                // Usar navigator.sendBeacon para garantir envio
                const token = localStorage.getItem('accessToken');

                // ✅ Só enviar se tiver token
                if (token && sessionIdRef.current) {
                    const blob = new Blob([JSON.stringify({
                        sessionId: sessionIdRef.current
                    })], { type: 'application/json' });

                    navigator.sendBeacon(
                        `${API_BASE_URL}/activity/end-session?token=${token}`,
                        blob
                    );
                }
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, []);

    return {
        isTracking: isTrackingRef.current,
        sessionId: sessionIdRef.current
    };
};